import { useEnabledFeatures } from '../../../../common/hooks/enabled-features/use-enabled-features';
import { Flex, Button } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { cloneDeep, debounce } from 'lodash';
import { useEsriMapLocationCautions } from '../../../hooks/use-esri-map-location-cautions';
import { useEsriMapStore } from '../../../hooks/use-esri-map-store';
import { LocationCautionsSection } from '../location-cautions-section';
import { LocationOverview } from './location-overview';
import { LocationAddressAndGpsSection } from '../location-address-and-gps-section';
import { LocationInfoSingleAgencySection } from './location-info-single-agency-section';
import { LocationInfoMultiAgencySection } from './location-info-multi-agency-section';
import { useBootstrapState } from '../../../../bootstrap/bootstrap-provider';
import { useLocationApi } from '../../../../location-select/hooks/use-location-api';
import { isValidLatLng } from '../../../../location/helpers';
import { GeoPointLocation } from '../../../../esri/types';
import { getUniqueIdFromLocationView } from '../../../../esri/helpers/locations';
import { LocationRelatedDataSection } from '../location-related-data-section';
import { getClosestLocation } from '../../../../common/helpers/get-closest-location';
import { NearbyCautionsSection } from '../nearby-cautions-section';
import { mapConsolidatedLocationViewToLocationStoreView } from '../../../../location-select/helpers/data-mapping';
import { PanelWrapper } from '../panel-wrapper';
import { useOpenEventPanel } from '../../../../event-panel/use-open-event-panel';
import { useUnitAssignment } from '../../../../unit-assignment/unit-assignment-context';
import { SelfInitiateButton } from '../self-initiate-button';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import { ClassificationStrategy } from '../../../../common/types';

export const LocationPanel = observer(function LocationPanel(): React.ReactElement {
    const mapStore = useEsriMapStore();
    const {
        multiAgencySubdivisions,
        enhancedRelatedData,
        nearbyCautionsOnMapEnabled,
        dispatchFromMap,
        selfInitiateEvent,
    } = useEnabledFeatures();
    const { searchLocations, isLoadingResults, resolveEntityLinksForLocation } = useLocationApi(
        multiAgencySubdivisions
            ? ClassificationStrategy.MULTI_AGENCY
            : ClassificationStrategy.SINGLE_AGENCY
    );
    const { cadDepartmentAgencyView } = useBootstrapState();
    const openEventPanel = useOpenEventPanel();
    const { isFirstResponder, isCad } = useApplicationType();
    const size = isFirstResponder ? 'lg' : 'md';
    const unitAssignment = useUnitAssignment();

    const geoPointLocation = mapStore.locationSearchSelectedGeoPointLocation;
    const geoPointAddress = mapStore.geoPointAddressForLocationSearch;

    const {
        placeName,
        displayLineFirst,
        displayLineSubPremises,
        displayLineOptionalMiddle,
        displayLineLast,
        latitude,
        longitude,
        areCoordinatesVerified,
        source,
    } = geoPointLocation || {};

    const departmentId = cadDepartmentAgencyView.departmentId;

    const address =
        geoPointLocation?.resolverSource === 'LAT_LONG'
            ? `${geoPointLocation.latitude},${geoPointLocation.longitude}`
            : geoPointLocation?.fullAddressKey;

    const { data: locationCautions } = useEsriMapLocationCautions({
        address: address ?? '',
        coordinates: { latitude, longitude, valid: true, validOrNull: true },
        departmentId,
    });

    const priorityOneNearbyGeoCautions = useMemo(() => {
        return locationCautions?.nearbyGeoCautions.filter(
            (caution) => caution.priority === 'Priority 1'
        );
    }, [locationCautions?.nearbyGeoCautions]);

    if (nearbyCautionsOnMapEnabled && priorityOneNearbyGeoCautions) {
        mapStore.setNearbyGeoCautions(priorityOneNearbyGeoCautions);
    }

    const isLoading = geoPointLocation ? false : isLoadingResults;
    const handleOpenDraftEventPanel = debounce(() => {
        openEventPanel({
            shouldFocusPanel: true,
            workspacePanelConfig: {
                panelState: {
                    location: cloneDeep(geoPointLocation),
                },
            },
        });
    }, 500);

    const showAddNewEventButtonInFooter = isCad && dispatchFromMap;
    const isAssignedToEvent = !!unitAssignment?.assignedEvent;
    const showSelfInitiateButtonInFooter =
        isFirstResponder && selfInitiateEvent && !isAssignedToEvent;

    useEffect(
        function searchGeoPointLocation() {
            if (!geoPointLocation && geoPointAddress) {
                let queryString = geoPointAddress.fullAddress;
                if (
                    !queryString &&
                    isValidLatLng(geoPointAddress.latitude, geoPointAddress.longitude)
                ) {
                    queryString = `${geoPointAddress.latitude},${geoPointAddress.longitude}`;
                }
                if (queryString) {
                    searchLocations(queryString)
                        .then((results) => {
                            const closestLocation = getClosestLocation(
                                results,
                                geoPointAddress.preferLatLngSourceLocation
                                    ? {
                                          latitude: geoPointAddress.latitude,
                                          longitude: geoPointAddress.longitude,
                                      }
                                    : undefined
                            );

                            if (closestLocation) {
                                resolveEntityLinksForLocation(closestLocation).then(
                                    (resolvedLocation) => {
                                        const consolidatedView = mapConsolidatedLocationViewToLocationStoreView(
                                            resolvedLocation
                                        );
                                        mapStore.setLocationSearchSelectedGeoPointLocation({
                                            ...consolidatedView,
                                            entityId: getUniqueIdFromLocationView(consolidatedView),
                                        } as GeoPointLocation);
                                    },
                                    () => mapStore.clearGeoPointLocationAndAddress()
                                );
                            } else {
                                mapStore.clearGeoPointLocationAndAddress();
                            }
                        })
                        .catch(() => {
                            mapStore.clearGeoPointLocationAndAddress();
                        });
                } else {
                    mapStore.clearGeoPointLocationAndAddress();
                }
            }
        },
        [
            geoPointAddress,
            geoPointLocation,
            searchLocations,
            mapStore,
            resolveEntityLinksForLocation,
        ]
    );

    const footerContent = showAddNewEventButtonInFooter ? (
        <Button
            leadingVisual="Add"
            disabled={isLoading}
            onClick={handleOpenDraftEventPanel}
            size={size}
        >
            New Event
        </Button>
    ) : showSelfInitiateButtonInFooter ? (
        <SelfInitiateButton geoPointLocation={cloneDeep(geoPointLocation)} size={size} />
    ) : null;

    return (
        <PanelWrapper testId="MAP_LOCATION_PANEL" footerContent={footerContent}>
            <LocationOverview isLoading={isLoading} />
            <Flex direction="column">
                <LocationAddressAndGpsSection
                    placeName={placeName}
                    displayLineFirst={displayLineFirst}
                    displayLineSubPremises={displayLineSubPremises}
                    displayLineOptionalMiddle={displayLineOptionalMiddle}
                    displayLineLast={displayLineLast}
                    geoLocationLatitude={latitude}
                    geoLocationLongitude={longitude}
                    areGeoLocationCoordinatesVerified={areCoordinatesVerified}
                    source={source}
                    clearSelectedNearbyGeoCautionCoordinates={
                        mapStore.clearSelectedNearbyGeoCautionCoordinates
                    }
                    size={size}
                />
                {!isLoading && !!geoPointLocation && (
                    <>
                        {multiAgencySubdivisions ? (
                            <LocationInfoMultiAgencySection />
                        ) : (
                            <LocationInfoSingleAgencySection />
                        )}
                        <LocationCautionsSection
                            cautions={locationCautions?.cautions}
                            size={size}
                        />
                        {nearbyCautionsOnMapEnabled && (
                            <NearbyCautionsSection
                                cautions={priorityOneNearbyGeoCautions}
                                setSelectedNearbyGeoCautionCoordinates={
                                    mapStore.setSelectedNearbyGeoCautionCoordinates
                                }
                                size={size}
                            />
                        )}
                        {enhancedRelatedData && (
                            <LocationRelatedDataSection geoPointLocation={geoPointLocation} />
                        )}
                    </>
                )}
            </Flex>
        </PanelWrapper>
    );
});
